import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

interface WavyTransitionProps {
  isActive: boolean;
  onTransitionComplete: () => void;
  buttonRect: DOMRect | null;
  children: React.ReactNode;
}

export function WavyTransition({
  isActive,
  onTransitionComplete,
  buttonRect,
  children,
}: WavyTransitionProps) {
  const [phase, setPhase] = useState<'idle' | 'expanding' | 'fading' | 'showing'>('idle');
  const [expandProgress, setExpandProgress] = useState(0);
  const [fadeProgress, setFadeProgress] = useState(0);
  const animationRef = useRef<number | null>(null);

  // Calculate center position from button in pixels
  const centerX = buttonRect 
    ? buttonRect.left + buttonRect.width / 2
    : (typeof window !== 'undefined' ? window.innerWidth / 2 : 400);
  const centerY = buttonRect 
    ? buttonRect.top + buttonRect.height / 2
    : (typeof window !== 'undefined' ? window.innerHeight - 100 : 700);

  // Calculate max radius needed to cover entire screen from the center point
  const maxRadius = useMemo(() => {
    if (typeof window === 'undefined') return 2000;
    const corners = [
      Math.sqrt(centerX * centerX + centerY * centerY),
      Math.sqrt((window.innerWidth - centerX) ** 2 + centerY ** 2),
      Math.sqrt(centerX ** 2 + (window.innerHeight - centerY) ** 2),
      Math.sqrt((window.innerWidth - centerX) ** 2 + (window.innerHeight - centerY) ** 2),
    ];
    return Math.max(...corners) * 1.2;
  }, [centerX, centerY]);

  // Generate an inky, blobby path
  const generateInkPath = useCallback((
    progress: number, 
    seed: number = 0,
    originX: number,
    originY: number,
    radius: number
  ) => {
    const baseRadius = radius * progress;
    if (baseRadius < 1) return '';
    
    const points: [number, number][] = [];
    const segments = 72;
    
    // Create multiple noise frequencies for organic look
    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      
      // Layer multiple sine waves for organic blobby effect
      const noise1 = Math.sin(angle * 3 + seed) * 0.15;
      const noise2 = Math.sin(angle * 7 + seed * 2.3) * 0.08;
      const noise3 = Math.sin(angle * 11 + seed * 1.7) * 0.05;
      const noise4 = Math.sin(angle * 5 + progress * Math.PI * 4) * 0.1 * progress;
      
      // Combine noises - more dramatic waves
      const noiseTotal = 1 + noise1 + noise2 + noise3 + noise4;
      const r = baseRadius * noiseTotal;
      
      const x = originX + r * Math.cos(angle);
      const y = originY + r * Math.sin(angle);
      
      points.push([x, y]);
    }
    
    // Create smooth bezier curve through points
    if (points.length < 3) return '';
    
    let path = `M ${points[0][0]} ${points[0][1]}`;
    
    for (let i = 0; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p3 = points[(i + 2) % points.length];
      
      // Catmull-Rom to Bezier conversion for smooth curves
      const tension = 0.3;
      const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
      
      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
    }
    
    path += ' Z';
    return path;
  }, []);

  // Animate expansion
  useEffect(() => {
    if (isActive && phase === 'idle') {
      setPhase('expanding');
      setExpandProgress(0);
      setFadeProgress(0);
      
      const startTime = performance.now();
      const duration = 1200;
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Custom easing - slow start, accelerate, then slow end
        const eased = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        
        setExpandProgress(eased);
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          // Start fade animation after a brief pause
          setTimeout(() => {
            setPhase('fading');
          }, 100);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [isActive, phase]);

  // Animate fade out
  useEffect(() => {
    if (phase === 'fading') {
      const startTime = performance.now();
      const duration = 800;
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease out for smooth fade
        const eased = 1 - Math.pow(1 - progress, 2);
        
        setFadeProgress(eased);
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setPhase('showing');
          onTransitionComplete();
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [phase, onTransitionComplete]);

  // Instant close when going back - no animation
  useEffect(() => {
    if (!isActive && phase !== 'idle') {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setPhase('idle');
      setExpandProgress(0);
      setFadeProgress(0);
    }
  }, [isActive, phase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Generate paths for multiple ink layers during expansion
  const inkPaths = useMemo(() => {
    return [
      { path: generateInkPath(expandProgress, 0, centerX, centerY, maxRadius), seed: 0 },
      { path: generateInkPath(expandProgress * 0.92, 1.5, centerX, centerY, maxRadius), seed: 1.5 },
      { path: generateInkPath(expandProgress * 0.84, 3.2, centerX, centerY, maxRadius), seed: 3.2 },
      { path: generateInkPath(expandProgress * 0.76, 4.8, centerX, centerY, maxRadius), seed: 4.8 },
      { path: generateInkPath(expandProgress * 0.68, 6.1, centerX, centerY, maxRadius), seed: 6.1 },
    ];
  }, [expandProgress, generateInkPath, centerX, centerY, maxRadius]);

  if (phase === 'idle') {
    return null;
  }

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 800;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 600;

  // Content is visible once ink is fully expanded
  const contentVisible = expandProgress >= 1;
  // Ink opacity during fade
  const inkOpacity = 1 - fadeProgress;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Content underneath - rendered immediately, visible once ink covers screen */}
      <div
        className="absolute inset-0"
        style={{
          opacity: contentVisible ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}
      >
        {children}
      </div>

      {/* SVG ink splat layers on top - fade out to reveal content */}
      {inkOpacity > 0 && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
          preserveAspectRatio="none"
          style={{ overflow: 'visible', opacity: inkOpacity }}
        >
          <defs>
            <linearGradient id="inkGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fb7185" />
              <stop offset="25%" stopColor="#f472b6" />
              <stop offset="50%" stopColor="#fbbf24" />
              <stop offset="75%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
            <linearGradient id="inkGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#a78bfa" />
              <stop offset="33%" stopColor="#f472b6" />
              <stop offset="66%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#4ade80" />
            </linearGradient>
            <linearGradient id="inkGradient3" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#38bdf8" />
              <stop offset="50%" stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#fb7185" />
            </linearGradient>
            <linearGradient id="inkGradient4" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="50%" stopColor="#34d399" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
            <linearGradient id="inkGradient5" x1="0%" y1="50%" x2="100%" y2="50%">
              <stop offset="0%" stopColor="#f472b6" />
              <stop offset="50%" stopColor="#fb7185" />
              <stop offset="100%" stopColor="#fbbf24" />
            </linearGradient>
          </defs>
          
          {/* Ink splat layers */}
          {inkPaths.map(({ path }, i) => {
            const baseOpacity = 1 - i * 0.15;
            return path ? (
              <path
                key={`ink-${i}`}
                d={path}
                fill={`url(#inkGradient${i + 1})`}
                opacity={baseOpacity}
              />
            ) : null;
          })}
        </svg>
      )}
    </div>
  );
}
